import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  FiBookOpen,
  FiClock,
  FiPlay,
  FiCheck,
  FiTrendingUp,
  FiBook,
  FiEye,
  FiBarChart,
  FiLayers,
} from 'react-icons/fi';
import {
  getCachedComprehensiveProgress,
  getReadingComprehensionProgressFromComprehensive,
  clearProgressCache,
  getCachedReadingComprehensionProgress,
  getCachedReadingComprehensionPassages,
} from './api/progressAPI';
import {
  API_BASE_URL,
  getAuthHeaders,
  redirectToLoginWithReturn,
} from '../../../utils/utils';
import { getStoredUser } from '../../../utils/authSession';
import { getErrorMessage } from '../../../utils/getErrorMessage';
import GetStartedWith from '../../../components/GetStartedWith';
import {
  formatReadingScorePercent,
  formatReadingTimeSpent,
} from '../../../components/readingComprehensionReview';
import ReadingTipsPanel from './ReadingTipsPanel';

// API functions now use cached versions from progressAPI.js

const resolveReadingUserEmail = () => {
  try {
    const legacyUser = JSON.parse(sessionStorage.getItem('user') || 'null');
    return legacyUser?.email || getStoredUser()?.email || null;
  } catch {
    return getStoredUser()?.email || null;
  }
};

// Helper function for other components to access Reading Comprehension progress
export const getReadingComprehensionProgressData = async () => {
  try {
    // Get comprehensive progress data from backend
    const comprehensiveProgress = await getCachedComprehensiveProgress();
    const backendData = getReadingComprehensionProgressFromComprehensive(
      comprehensiveProgress
    );

    return {
      totalPassages: backendData.totalPassages,
      completedPassages: backendData.totalPassages,
      accuracy: backendData.accuracy,
      averageScore: backendData.accuracy,
      totalCorrect: backendData.correctAnswers,
      totalQuestions: backendData.totalQuestions,
      lastUpdated: backendData.lastUpdated,
      averageReadingSpeed: backendData.averageReadingSpeed,
      targetReadingSpeed: backendData.targetReadingSpeed,
      passagesByType: backendData.passagesByType,
      questionsByType: backendData.questionsByType,
      averageTimePerPassage: backendData.averageTimePerPassage,
      targetTimePerPassage: backendData.targetTimePerPassage,
      targetAccuracy: backendData.targetAccuracy,
      improvementAreas: backendData.improvementAreas,
      recentSessions: backendData.recentSessions,
      readinessScore: backendData.readinessScore,
    };
  } catch (error) {
    console.error('Error getting reading comprehension progress:', error);
    return {
      totalPassages: 0,
      completedPassages: 0,
      accuracy: 0,
      averageScore: 0,
      totalCorrect: 0,
      totalQuestions: 0,
      lastUpdated: null,
      averageReadingSpeed: 0,
      targetReadingSpeed: 350,
      passagesByType: {},
      questionsByType: {},
      averageTimePerPassage: 0,
      targetTimePerPassage: 420,
      targetAccuracy: 75.0,
      improvementAreas: [],
      recentSessions: [],
      readinessScore: 0,
    };
  }
};

export default function ReadingComprehension() {
  const navigate = useNavigate();
  const authRedirectStartedRef = useRef(false);
  const [selectedTest, setSelectedTest] = useState(null);
  const [readingTests, setReadingTests] = useState([]);
  const [testResults, setTestResults] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Refs to track ongoing API requests
  const ongoingRequests = useRef({
    passages: null,
    progress: null,
  });

  const redirectToLogin = (
    message = 'Votre session a expire. Veuillez vous reconnecter.'
  ) => {
    if (authRedirectStartedRef.current) {
      return;
    }

    authRedirectStartedRef.current = true;
    redirectToLoginWithReturn({
      navigate,
      redirectTo: window.location.pathname + window.location.search,
      message,
    });
  };

  // Enhanced API functions with duplicate prevention and 10-minute caching
  const getReadingComprehensionPassagesWithRef = async (
    difficulty = null,
    topic = null,
    limit = 100
  ) => {
    const requestKey = `passages_${difficulty || 'all'}_${topic || 'all'}_${limit}`;

    // If request is already in progress, return the existing promise
    if (ongoingRequests.current[requestKey]) {
      return ongoingRequests.current[requestKey];
    }

    // Get user email
    const userEmail = resolveReadingUserEmail();

    // Create new request promise using cached function
    const requestPromise = getCachedReadingComprehensionPassages(
      userEmail,
      difficulty,
      topic,
      limit
    );

    // Store the promise in ref
    ongoingRequests.current[requestKey] = requestPromise;

    // Clean up the ref when request completes (success or failure)
    requestPromise.finally(() => {
      ongoingRequests.current[requestKey] = null;
    });

    return requestPromise;
  };

  const getReadingComprehensionProgressWithRef = async () => {
    const requestKey = 'progress';

    // If request is already in progress, return the existing promise
    if (ongoingRequests.current[requestKey]) {
      return ongoingRequests.current[requestKey];
    }

    // Get user email
    const userEmail = resolveReadingUserEmail();

    // Create new request promise using cached function
    const requestPromise = getCachedReadingComprehensionProgress(userEmail);

    // Store the promise in ref
    ongoingRequests.current[requestKey] = requestPromise;

    // Clean up the ref when request completes (success or failure)
    requestPromise.finally(() => {
      ongoingRequests.current[requestKey] = null;
    });

    return requestPromise;
  };

  // Load reading comprehension data on component mount
  useEffect(() => {
    const loadReadingData = async () => {
      try {
        setLoading(true);

        // Fetch passages and progress data
        const [passages, progressData] = await Promise.all([
          getReadingComprehensionPassagesWithRef(),
          getReadingComprehensionProgressWithRef(),
        ]);

        // Transform backend data to match component structure
        const transformedTests = passages.map((passage) => ({
          id: passage.id || passage.passage_id,
          title: passage.title,
          description: passage.description,
          difficulty: passage.difficulty_level || 'Medium',
          duration: `${passage.duration_minutes} minutes`,
          questions: passage.question_count || passage.questions?.length || 0,
          topic: passage.topic,
          passage: passage.content,
          questions_data: passage.questions || [],
          completed: false,
          score: null,
        }));

        setReadingTests(transformedTests);

        // Process completed passages for results
        const resultsMap = {};
        if (progressData.completed_passages) {
          progressData.completed_passages.forEach((result) => {
            resultsMap[result.passage_id] = {
              score: result.score,
              correctAnswers: result.number_correct_answers,
              totalQuestions: result.total_questions,
              timeSpent: result.time_spent_seconds,
              readingTime: result.reading_time_seconds,
              completedAt: result.completed_at,
              answers: result.user_answers || [], // Include user's answers for review mode
              passageId: result.passage_id,
            };
          });
        }
        setTestResults(resultsMap);

        console.log('reading test & results', passages, progressData);
      } catch (error) {
        console.error('Error loading reading comprehension data:', error);
        if (error?.message === 'Authentication failed') {
          redirectToLogin();
          return;
        }
        setError(error.message);
      } finally {
        setLoading(false);
      }
    };

    loadReadingData();

    // Listen for custom event when test is completed
    const handleTestCompleted = (event) => {
      console.log('Test completed event received, reloading data...');
      loadReadingData();
    };

    window.addEventListener('greReadingTestCompleted', handleTestCompleted);

    return () => {
      window.removeEventListener(
        'greReadingTestCompleted',
        handleTestCompleted
      );
    };
  }, []);

  const handleStartTest = (test) => {
    navigate(`/exams/gre/reading-comprehension/${test.id}`);
  };

  const handleReviewTest = (test) => {
    navigate(`/exams/gre/reading-comprehension/${test.id}?review=true`);
  };

  const getDifficultyColor = (difficulty) => {
    switch (difficulty.toLowerCase()) {
      case 'easy':
        return '#10B981';
      case 'medium':
        return '#F59E0B';
      case 'hard':
        return '#EF4444';
      default:
        return '#6366F1';
    }
  };

  const getTopicIcon = (topic) => {
    switch (topic) {
      case 'Natural Science':
        return <FiTrendingUp />;
      case 'Humanities':
        return <FiBook />;
      case 'Social Science':
        return <FiBarChart />;
      default:
        return <FiBookOpen />;
    }
  };

  if (loading) {
    return (
      <div
        style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '400px',
          color: 'var(--liwaza-on-surface)',
        }}
      >
        <div className="layout-loading">
          <div className="loading-spinner"></div>
          <p>Loading reading comprehension passages...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '400px',
          color: 'var(--liwaza-error)',
          textAlign: 'center',
          padding: '2rem',
        }}
      >
        <h2>Error Loading Passages</h2>
        <p>{getErrorMessage(error, 'Failed to load passages.')}</p>
        <button
          onClick={() => window.location.reload()}
          style={{
            background: 'var(--liwaza-primary)',
            color: 'white',
            border: 'none',
            padding: '0.75rem 1.5rem',
            borderRadius: '8px',
            cursor: 'pointer',
            marginTop: '1rem',
          }}
        >
          Retry
        </button>
      </div>
    );
  }

  const renderPracticeTests = () => (
    <div>
      <h2
        style={{
          color: 'var(--liwaza-on-surface)',
          marginBottom: '1.5rem',
        }}
      >
        Practice Tests
      </h2>

      {readingTests.length === 0 ? (
        <div
          style={{
            textAlign: 'center',
            padding: '3rem',
            color: 'var(--liwaza-on-surface-variant)',
          }}
        >
          <FiBookOpen
            size={48}
            style={{ marginBottom: '1rem', opacity: 0.5 }}
          />
          <p>No reading comprehension passages available.</p>
        </div>
      ) : (
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(350px, 1fr))',
            gap: '1.5rem',
          }}
        >
          {readingTests.map((test) => {
            const testResult = testResults[test.id];
            const isCompleted = testResult !== null && testResult !== undefined;
            const timeSpent = isCompleted ? testResult.timeSpent : null;
            const correctAnswers = isCompleted
              ? testResult.correctAnswers
              : null;
            const totalQuestions = isCompleted
              ? testResult.totalQuestions
              : null;
            const scoreLabel = isCompleted
              ? formatReadingScorePercent(
                  correctAnswers,
                  totalQuestions,
                  testResult.score
                )
              : null;

            return (
              <div
                key={test.id}
                style={{
                  background: 'var(--liwaza-surface-container)',
                  padding: '1.5rem',
                  borderRadius: '12px',
                  border: `1px solid ${isCompleted ? 'var(--liwaza-success-container)' : 'var(--liwaza-outline-variant)'}`,
                  display: 'flex',
                  flexDirection: 'column',
                  height: '100%',
                }}
              >
                <div style={{ flex: 1 }}>
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      marginBottom: '1rem',
                    }}
                  >
                    <h3
                      style={{
                        color: 'var(--liwaza-on-surface)',
                        fontSize: '1.2rem',
                        margin: 0,
                      }}
                    >
                      {test.title}
                    </h3>
                    {isCompleted && (
                      <div
                        style={{
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'flex-end',
                          gap: '0.25rem',
                        }}
                      >
                        <span
                          style={{
                            background: 'var(--liwaza-success-container)',
                            color: 'var(--liwaza-success)',
                            padding: '0.25rem 0.5rem',
                            borderRadius: '4px',
                            fontSize: '0.8rem',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '0.25rem',
                          }}
                        >
                          <FiCheck /> Completed
                        </span>
                        <span
                          style={{
                            background: 'var(--liwaza-primary-container)',
                            color: 'var(--liwaza-primary)',
                            padding: '0.25rem 0.5rem',
                            borderRadius: '4px',
                            fontSize: '0.8rem',
                            fontWeight: 600,
                          }}
                        >
                          Score: {scoreLabel}%
                        </span>
                      </div>
                    )}
                  </div>

                  <p
                    style={{
                      color: 'var(--liwaza-on-surface-variant)',
                      fontSize: '0.9rem',
                      marginBottom: '1rem',
                      lineHeight: 1.5,
                    }}
                  >
                    {test.description}
                  </p>

                  <div
                    style={{
                      display: 'flex',
                      flexWrap: 'wrap',
                      gap: '0.5rem',
                      marginBottom: '1rem',
                    }}
                  >
                    <span
                      style={{
                        background: getDifficultyColor(test.difficulty),
                        color: 'white',
                        padding: '0.25rem 0.5rem',
                        borderRadius: '4px',
                        fontSize: '0.8rem',
                      }}
                    >
                      {test.difficulty}
                    </span>
                    <span
                      style={{
                        background: 'var(--liwaza-secondary-container)',
                        color: 'var(--liwaza-on-secondary-container)',
                        padding: '0.25rem 0.5rem',
                        borderRadius: '4px',
                        fontSize: '0.8rem',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.25rem',
                      }}
                    >
                      {getTopicIcon(test.topic)}
                      {test.topic}
                    </span>
                  </div>

                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      color: 'var(--liwaza-on-surface-variant)',
                      fontSize: '0.9rem',
                      marginBottom: '1.5rem',
                    }}
                  >
                    <span
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.25rem',
                      }}
                    >
                      <FiClock /> {test.duration}
                    </span>
                    <span
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.25rem',
                      }}
                    >
                      <FiLayers /> {test.questions} questions
                    </span>
                  </div>

                  {isCompleted && testResult && (
                    <div
                      style={{
                        background: 'var(--liwaza-success-container)',
                        padding: '1rem',
                        borderRadius: '8px',
                        marginBottom: '1rem',
                        border: '1px solid var(--liwaza-success)',
                      }}
                    >
                      <div
                        style={{
                          marginBottom: '0.5rem',
                          fontSize: '0.9rem',
                          fontWeight: 600,
                          color: 'var(--liwaza-success)',
                        }}
                      >
                        Test Results:
                      </div>
                      <div
                        style={{
                          display: 'grid',
                          gridTemplateColumns: '1fr 1fr',
                          gap: '0.5rem',
                          fontSize: '0.85rem',
                          color: 'var(--liwaza-on-success-container)',
                        }}
                      >
                        <div>Score: {scoreLabel}%</div>
                        <div>
                          Correct: {correctAnswers}/{totalQuestions}
                        </div>
                        <div>
                          Completed:{' '}
                          {new Date(
                            testResult.completedAt
                          ).toLocaleDateString()}
                        </div>
                        <div>Time: {formatReadingTimeSpent(timeSpent)}</div>
                      </div>
                    </div>
                  )}
                </div>

                <div style={{ display: 'flex', gap: '0.5rem' }}>
                  {isCompleted ? (
                    <>
                      <button
                        onClick={() => handleReviewTest(test)}
                        style={{
                          flex: 1,
                          background: 'var(--liwaza-secondary)',
                          color: 'white',
                          border: 'none',
                          padding: '0.75rem',
                          borderRadius: '8px',
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          gap: '0.5rem',
                          fontSize: '1rem',
                          fontWeight: 500,
                        }}
                      >
                        <FiEye /> Review
                      </button>
                      <button
                        onClick={() => handleStartTest(test)}
                        style={{
                          flex: 1,
                          background: 'var(--liwaza-primary)',
                          color: 'white',
                          border: 'none',
                          padding: '0.75rem',
                          borderRadius: '8px',
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          gap: '0.5rem',
                          fontSize: '1rem',
                          fontWeight: 500,
                        }}
                      >
                        <FiPlay /> Retake
                      </button>
                    </>
                  ) : (
                    <button
                      onClick={() => handleStartTest(test)}
                      style={{
                        width: '100%',
                        background: 'var(--liwaza-primary)',
                        color: 'white',
                        border: 'none',
                        padding: '0.75rem',
                        borderRadius: '8px',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '0.5rem',
                        fontSize: '1rem',
                        fontWeight: 500,
                      }}
                    >
                      <FiPlay /> Start Test
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );

  return (
    <div style={{ padding: '2rem' }}>
      <div style={{ marginBottom: '2rem' }}>
        <h1
          style={{
            fontSize: '2rem',
            color: 'var(--liwaza-on-surface)',
            marginBottom: '1rem',
          }}
        >
          Reading Comprehension
        </h1>
        <p
          style={{
            color: 'var(--liwaza-on-surface-variant)',
            fontSize: '1.1rem',
            marginBottom: '2rem',
          }}
        >
          Master GRE Reading Comprehension with passages from Natural Science,
          Humanities, and Social Science topics. Practice proven strategies and
          track your progress.
        </p>
      </div>

      <ReadingTipsPanel />

      {/* Get Started With Component */}
      <div style={{ margin: '3rem 0 2rem 0' }}>
        <GetStartedWith
          title="Need help with Reading Comprehension strategies?"
          spaceName="Verbal vault"
          humanCoachTitle="Talk to a GRE coach"
          liwazaAITitle="Ask AI"
          communityTitle="Ask other students"
          showHumanCoach={true}
          showLiwazaAI={true}
          showCommunity={true}
        />
      </div>

      {renderPracticeTests()}

    </div>
  );
}
